import { getCookie } from '../lib/cookie_reader';
import Logger from '../lib/logger';
import { COOKIE_GROUP_ID_MAP, getActiveGroups, isOneTrustEnabled, parseActiveGroups } from '../lib/onetrust_consent';
import {
  PAGEVIEW_KEY,
  canPersistPageview,
  getAndIncrementPageview,
  pageviewInactivityMs,
} from '../lib/pageview_counter';
import { isSaleOrTargetedAdvertisingOptedOut, OOOS_KEY, OOOS_VALUE } from '../lib/sale_opt_out';
import PluginBase from './base';

/**
 * Listens for OneTrust Consent Data
 * Initializes with default consent data and updates when OneTrust data is available
 * Sets consent information on the ConcertAds variable: concertAds.oneTrustConsentData
 */
export default class OneTrustIntegration extends PluginBase {
  onSettingsLoaded() {
    if (!this.isOneTrustEligible()) return;
    Logger.log('Installing plugin: One Trust Integration');

    // Initialize with default consent data (denied by default)
    this.initializeDefaultConsentData();

    // Always listen for later consent changes — e.g. a GDPR visitor who loads with only
    // `,C0001,` active and accepts analytics (C0002) from the banner afterwards. Listening only
    // when OnetrustActiveGroups was absent at load missed that grant, so the delayed pageview
    // count (and any other consent-gated behaviour) never ran for it.
    //
    // onSettingsLoaded re-runs on every install/reinstall (gallery, SPA nav) via `settingsAvailable`,
    // but this plugin instance persists for the app lifetime, so attach the window listener only
    // once. Otherwise handlers accumulate and re-apply consent + GAM targeting on every later grant.
    if (!this.consentListenerAttached) {
      window.addEventListener('OneTrustGroupsUpdated', event => {
        this.setOneTrustConsentData(parseActiveGroups(event.detail), this.app);
      });
      this.consentListenerAttached = true;
    }

    // Apply whatever OneTrust already reported at load, including the OptanonConsent
    // cookie fallback when OnetrustActiveGroups is not on the window yet.
    const activeGroups = getActiveGroups();
    if (activeGroups) {
      this.setOneTrustConsentData(activeGroups, this.app);
    }

    // ADS-49: `ooos` comes from usprivacy / GPP, not OneTrust groups. A returning
    // visitor may already have those signals before groups fire. Retry GPP/USP
    // attach here so a CMP that was missing at the first install is picked up
    // on reinstall instead of leaking extra listeners.
    this.setOoosTargeting(this.app);
    this.listenForPrivacySignalUpdates();
  }

  /**
   * Check if we want to load the OneTrust Integration Plugin
   */
  isOneTrustEligible() {
    return isOneTrustEnabled(this.app.settings);
  }

  /**
   * Initialize default consent data (denied by default for privacy)
   * This ensures the app has consent data available immediately
   */
  initializeDefaultConsentData() {
    const defaultConsentData = {
      oneTrustGroupArray: [],
      ad_storage: '',
      ad_user_data: '',
      ad_personalization: '',
      analytics_storage: '',
    };

    this.app.oneTrustConsentData = defaultConsentData;
    Logger.log('OneTrust: Initialized with default consent data (denied)');
  }

  /**
   *  Set One Trust Consent Data on Concert Ads app
   * @param {Array} oneTrustGroupArray
   * @param {Object} app
   * @returns {Object} oneTrustConsentData
   */
  setOneTrustConsentData(oneTrustGroupArray, app) {
    const oneTrustConsentData = {
      oneTrustGroupArray,
    };

    for (let property in COOKIE_GROUP_ID_MAP) {
      oneTrustConsentData[property] = oneTrustGroupArray.includes(COOKIE_GROUP_ID_MAP[property]) ? 'granted' : 'denied';
    }

    app.oneTrustConsentData = oneTrustConsentData;

    // Set targeting variable for cookie consent
    this.setCookieConsentTargeting(oneTrustConsentData, app);
    this.persistPageviewIfGranted(oneTrustConsentData, app);
    this.setOoosTargeting(app);
    this.listenForPrivacySignalUpdates();

    Logger.log('OneTrust: Updated consent data', oneTrustConsentData);
  }

  /**
   * Set cookie-consent targeting variable based on OneTrust consent data
   * @param {Object} oneTrustConsentData
   * @param {Object} app
   */
  setCookieConsentTargeting(oneTrustConsentData, app) {
    let cookieConsentValue = null;

    if (oneTrustConsentData.oneTrustGroupArray?.length) {
      // Check if any consent is denied - if so, overall status is 'denied', otherwise 'granted'
      const hasDeniedConsent = Object.values(oneTrustConsentData).includes('denied');
      cookieConsentValue = hasDeniedConsent ? 'denied' : 'granted';
    }

    app.addVariable('cookie_consent', cookieConsentValue);
    Logger.log(`OneTrust: Set cookie_consent targeting to: ${cookieConsentValue}`);
  }

  /**
   * ADS-65 — when analytics storage (C0002) is granted, make sure this page's `pageview` has been
   * counted. If `_init` already counted it (consent was known at boot, `app.pageviewCounted`), then
   * re-affirming consent must not increment again, so this is a no-op. If the CMP was not ready at
   * boot, `_init` left the page at the uncounted default; do the real get-and-increment now — which
   * reads storage, so a higher unexpired session count is carried forward rather than overwritten
   * with `1` — then republish the KVP so GAM targeting reflects the corrected value.
   *
   * @param {Object} oneTrustConsentData
   * @param {Object} app
   */
  persistPageviewIfGranted(oneTrustConsentData, app) {
    if (!canPersistPageview(oneTrustConsentData) || app.pageviewCounted) return;

    const value = getAndIncrementPageview({
      canPersist: true,
      inactivityMs: pageviewInactivityMs(app.settings?.pageviewInactivityMinutes),
    });

    app.pageview = value;
    app.pageviewCounted = true;
    app.addVariable(PAGEVIEW_KEY, value);
  }

  /**
   * ADS-49 — publish `ooos=y` when the user opted out of sale / targeted ads
   * so AdOps can target away (`ooos` is none of `y`). Atlas set this from GPP;
   * we read the IAB `usprivacy` cookie and parsed GPP from the CMP OneTrust
   * already loaded. `cookie_consent` stays a separate OneTrust-group KVP.
   *
   * @param {Object} app
   */
  setOoosTargeting(app) {
    const optedOut = isSaleOrTargetedAdvertisingOptedOut({
      usPrivacy: this.readUsPrivacy(),
      gppParsedSections: this.readGppParsedSections(),
    });

    if (optedOut) {
      app.addVariable(OOOS_KEY, OOOS_VALUE);
      Logger.log(`OneTrust: Set ${OOOS_KEY} targeting to: ${OOOS_VALUE}`);
      return;
    }

    // Do not set ooos=n — AdOps targets `ooos` is none of `y`, which needs the key absent.
    if (typeof app.removeVariable === 'function') {
      app.removeVariable(OOOS_KEY);
      Logger.log(`OneTrust: Cleared ${OOOS_KEY} targeting`);
    }
  }

  /**
   * IAB / OneTrust store the USP string as `usprivacy` (no underscore). Older
   * embeds and the `__uspapi` window field still use `us_privacy`.
   *
   * @returns {string|undefined}
   */
  readUsPrivacy() {
    const fromCookie = getCookie('usprivacy') || getCookie('us_privacy');
    if (fromCookie) return fromCookie;
    if (typeof window.us_privacy === 'string') return window.us_privacy;
    this.refreshUspString();
    return this.uspString;
  }

  /**
   * `__uspapi('getUSPData')` is a one-shot, not a listener. Re-query on every
   * read so a mid-page opt-in overwrites or clears the last string instead of
   * pinning `ooos=y` from the first callback.
   */
  refreshUspString() {
    if (typeof window.__uspapi !== 'function') return;

    try {
      window.__uspapi('getUSPData', 1, (uspData, success) => {
        this.uspString = success && typeof uspData?.uspString === 'string' ? uspData.uspString : undefined;
      });
    } catch (e) {
      this.uspString = undefined;
    }
  }

  /**
   * OneTrust implements `__gpp`. The ping callback is often sync; if it is not,
   * `listenForPrivacySignalUpdates` will republish when the CMP fires.
   *
   * @returns {Record<string, Object|Object[]>|undefined}
   */
  readGppParsedSections() {
    if (typeof window.__gpp === 'function') {
      try {
        window.__gpp('ping', data => {
          if (data?.parsedSections) this.gppParsedSections = data.parsedSections;
        });
      } catch (e) {
        // CMP missing or ping not ready; last parsed sections still apply.
      }
    }
    return this.gppParsedSections;
  }

  /**
   * Attach GPP / USP listeners once each, and retry when the CMP was not on
   * the page at the first install. Do not add a second OneTrustGroupsUpdated
   * handler — the #520 listener already re-runs consent + ooos.
   */
  listenForPrivacySignalUpdates() {
    this.attachGppListener();
    this.attachUspCallback();
  }

  attachGppListener() {
    if (this.gppListenerAttached) return;
    if (typeof window.__gpp !== 'function') return;

    try {
      window.__gpp('addEventListener', (evt, success) => {
        if (!success) return;
        const parsed = evt?.pingData?.parsedSections || evt?.data?.parsedSections;
        if (parsed) this.gppParsedSections = parsed;
        this.setOoosTargeting(this.app);
      });
      this.gppListenerAttached = true;
    } catch (e) {
      // CMP missing or ping not ready; cookie / later group events still apply.
    }
  }

  attachUspCallback() {
    if (typeof window.__uspapi !== 'function') return;

    try {
      window.__uspapi('getUSPData', 1, (uspData, success) => {
        this.uspString = success && typeof uspData?.uspString === 'string' ? uspData.uspString : undefined;
        this.setOoosTargeting(this.app);
      });
    } catch (e) {
      // USP API unavailable.
    }
  }
}
